"use client"

import {
  Stack,
  Box,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Input,
  FormLabel,
  Button,
  Spinner,
  IconButton,
} from "@chakra-ui/react"
import "services/Recruit/assets/css/CreateJobOpening/style.css"
import { ArrowBackIcon, CloseIcon, DeleteIcon, DownloadIcon } from "@chakra-ui/icons"
import { globalStyles } from "common/theme/styles"
import { AttachmentIcon } from "services/Recruit/assets/svg/AttachmentIcon"
import { Field, Formik, Form } from "formik"
import { useEffect, useState, useMemo, useCallback } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { constant } from "common/constant"
import FileUpload from "common/components/fileUpload/FileUpload"
import ActionNavbar from "common/components/actionBar/ActionNavbar"
import { useDispatch, useSelector } from "react-redux"
import { getDepartments } from "common/redux/reducers/departmentSlice"
import { fetchUserData } from "common/redux/reducers/userSlice"
import { recruitConstant } from "services/Recruit/constant"
import { getApi } from "common/services/api"
import { API_PATHS } from "common/services/apiPaths"
import { toast } from "react-toastify"
import { cleanFormDataCandidate } from "common/utils/formDataCleanerCandidate"
import { downloadAttachment } from "common/redux/reducers/candidateSlice"
import { MAX_ATTACHMENT_FILE_SIZE, MAX_ATTACHMENT_FILE_SIZE_MB } from "services/Crm/utils/calcUtils"
import { ACCEPTED_DOCUMENT_TYPES } from "services/Crm/utils/calcUtils"
import * as Yup from "yup"
import { capitalizeWords } from "common/utils/textFormatters"
import { HasAccess } from "common/utils/accessUtils"
import { accessModuleTitles } from "common/constant"
import { addReferral } from "common/redux/reducers/referralSlice"
import { referralValidationSchema } from "services/Recruit/schema/referralSchema"
import { updateReferral } from "common/redux/reducers/referralSlice"
import { REFERRAL_INITIAL_VALUES } from "common/initialValues/formInitialValues"
import ReactSelect from "react-select"
import { formatJobTitle } from "services/Crm/utils/calcUtils"
import { referralTabs } from "common/constant"
import S3Helper from "common/utils/s3-helper"
import { validateS3File } from "services/Crm/utils/calcUtils"
import { generateS3Path } from "services/Crm/utils/calcUtils"
import axios from "axios"
import { removeTimestamp } from "services/Crm/utils/calcUtils"
import { extractS3KeyFlexible } from "services/Crm/utils/calcUtils"
import { isFormDirty } from "common/utils/formUtils"
import { fileFieldMapping } from "services/Crm/utils/calcUtils"

const ReferralsCreate = () => {
  const colors = globalStyles.colors
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id } = useParams()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [highestTabReached] = useState(0)
  const { addLoading } = useSelector((state) => state.referral)
  const { users } = useSelector((state) => state.users)
  const [editLoading, setEditLoading] = useState(false)
  const [referralData, setReferralData] = useState(null)
  const [showSizeError, setShowSizeError] = useState({
    resume: false,
  })
  const [referralsPermission, myReferralsPermission] = HasAccess([
    accessModuleTitles.Referrals,
    accessModuleTitles.MyReferrals,
  ])
  const permission = referralsPermission || myReferralsPermission
  const [isOverviewMode, setIsOverviewMode] = useState(false)
  const location = useLocation()
  const { mode } = location.state || {}
  const [jobOpenings, setJobOpenings] = useState([])
  const s3HelperInstance = S3Helper.getInstance();
  const [originalReferralData, setOriginalReferralData] = useState(null);
  const [removedFiles, setRemovedFiles] = useState({
    resume: false
  });

  const [pendingUploads, setPendingUploads] = useState({
    resume: null
  })

  const [pendingDeletions, setPendingDeletions] = useState({
    resume: null, // Will store { key, presignedUrl }
  });
  const [uploadingFiles, setUploadingFiles] = useState({
    resume: false
  })
  const modeType = sessionStorage.getItem("mode");
  const isEditMode = useMemo(() => {
    return modeType === "edit"
  }, [])

  const isEmployeeActiveRole = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const activeRoleName = String(user?.activeRoleId?.roleName || user?.role || "").toLowerCase();
      return activeRoleName === "employee";
    } catch {
      return false;
    }
  }, []);

  const canModify = useMemo(() => {
    if (isOverviewMode) return false;
    // Create mode: allow RBAC create, and allow employee referrals creation explicitly.
    if (!id) return !!permission?.create || isEmployeeActiveRole;
    // Edit mode: requires update permission.
    return !!permission?.update;
  }, [id, isEmployeeActiveRole, isOverviewMode, permission?.create, permission?.update]);
  useEffect(() => {
    const mode = sessionStorage.getItem("mode")

    if (mode === "overview") {
      setIsOverviewMode(true)
    } else {
      setIsOverviewMode(false)
    }
  }, [])

  // Define validation schemas for each tab
  const tabValidationSchemas = useMemo(
    () => ({
      0: Yup.object().shape({
        referringForJob: Yup.string().required("Job opening is required"),

        name: Yup.string()
          .required("Name is required")
          .min(2, "Name must be at least 2 characters")
          .max(255, "Name must not exceed 255 characters")
          .test('no-only-spaces', 'Name cannot contain only spaces', (value) => {
            if (!value) return false;
            return value.trim().length > 0;
          })
          .test('no-special-characters', 'Name can only contain letters and spaces', (value) => {
            if (!value || value === '') return true;
            return /^[a-zA-Z\s]+$/.test(value);
          })
          .test('contains-letter', 'Name must contain at least one letter', (value) => {
            if (!value || value === '') return true;
            return /[a-zA-Z]/.test(value);
          }),

        email: Yup.string()
          .email("Invalid email address")
          .required("Email is required"),

        phoneNumber: Yup.string()
          .required("Phone number is required")
          .test('no-only-spaces', 'Phone number cannot contain only spaces', (value) => {
            if (!value) return false;
            return value.trim().length > 0;
          })
          .test('phone-format', 'Please enter a valid Indian mobile number (exactly 10 digits starting with 6, 7, 8, or 9)', (value) => {
            if (!value || value.trim() === '') return true;
            const digitsOnly = value.replace(/\D/g, '');
            return digitsOnly.length === 10 && /^[6-9]/.test(digitsOnly);
          }),

        currentEmployer: Yup.string()
          .nullable()
          .max(100, "Current employer must not exceed 100 characters")
          .test('no-only-spaces', 'Current employer cannot contain only spaces', (value) => {
            if (!value) return true;
            return value.trim().length > 0;
          }),

        experience: Yup.number()
          .nullable()
          .transform((value, originalValue) => originalValue === "" ? null : value)
          .min(0, "Experience cannot be negative")
          .test(
            "max-one-decimal-place",
            "Experience can have at most 1 decimal place",
            (value) => value == null || Number.isInteger(value * 10)
          )
          .max(45, "Experience must not exceed 45 years"),

        resume: Yup.string().required("Resume is required"),

        relationWithEmployee: Yup.string()
          .required("Relation with employee is required")
          .max(100, "Relation with employee must not exceed 100 characters")
          .test('no-only-spaces', 'Relation with employee cannot contain only spaces', (value) => {
            if (!value) return false;
            return value.trim().length > 0;
          }),

        referralFeedback: Yup.string()
          .nullable()
          .test('word-count', 'Feedback must be between 1 and 500 words', function (value) {
            if (!value) return true;
            const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
            return wordCount >= 1 && wordCount <= 500;
          }),
      }),
    }),
    [],
  )

  // Function to check if a specific tab is valid
  const isTabValid = useCallback(
    (values, tabIndex) => {
      try {
        const hasResume = pendingUploads.resume ||
          values.resume ||
          (id && referralData?.resume && !removedFiles.resume);

        if (!hasResume) return false;

        tabValidationSchemas[tabIndex].validateSync(values, {
          abortEarly: false,
        })
        return true
      } catch (error) {
        return false
      }
    },
    [tabValidationSchemas, pendingUploads, id, referralData, removedFiles],
  )

  // Function to get tab navigation states with global validation tracking
  const getTabStates = useCallback(
    (values, dirty, isValid, currentTabIndex) => {
      const tabStates = {}

      // Find the first invalid tab among visited tabs
      let firstInvalidTab = null
      for (let i = 0; i <= highestTabReached; i++) {
        if (!isTabValid(values, i)) {
          firstInvalidTab = i
          break
        }
      }

      for (let i = 0; i <= 0; i++) {
        if (i < currentTabIndex) {
          // Previous tabs are always accessible
          tabStates[i] = { isAccessible: true, isValid: isTabValid(values, i) }
        } else if (i === currentTabIndex) {
          // Current tab - check if it's valid for navigation
          const currentTabValid = isTabValid(values, i)
          tabStates[i] = {
            isAccessible: true,
            isValid: currentTabValid,
            canProceed: currentTabValid,
          }
        } else if (firstInvalidTab !== null && i > firstInvalidTab) {
          // Tabs after first invalid tab - not accessible
          tabStates[i] = {
            isAccessible: false,
            isValid: false,
            canProceed: false,
          }
        } else if (i <= highestTabReached && firstInvalidTab === null) {
          // Previously visited tabs with no invalid tabs - accessible
          tabStates[i] = {
            isAccessible: true,
            isValid: isTabValid(values, i),
            canProceed: false,
          }
        } else if (i === currentTabIndex + 1 && firstInvalidTab === null) {
          // Next tab - accessible only if no invalid tabs exist
          tabStates[i] = {
            isAccessible: true,
            isValid: false,
            canProceed: false,
          }
        } else {
          // Future tabs beyond next - not accessible
          tabStates[i] = {
            isAccessible: false,
            isValid: false,
            canProceed: false,
          }
        }
      }

      return tabStates
    },
    [isTabValid, highestTabReached],
  )

  const handleTabChange = useCallback(
    (index, values, dirty, isValid) => {
      // Allow going back to previous tabs without validation
      if (index < activeTabIndex) {
        setActiveTabIndex(index)
        return
      }

      // If trying to stay on the same tab, allow it
      if (index === activeTabIndex) {
        return
      }

      // Check if navigation is allowed based on global validation state
      const tabStates = getTabStates(values, dirty, isValid, activeTabIndex)

      if (tabStates[index]?.isAccessible) {
        setActiveTabIndex(index)
      } else {
        toast.error("Please complete the current step before proceeding")
      }
    },
    [activeTabIndex, getTabStates],
  )

  useEffect(() => {
    dispatch(getDepartments())
    dispatch(fetchUserData())

    // Fetch job openings for the dropdown
    const fetchJobOpenings = async () => {
      try {
        const result = await getApi(`${API_PATHS.RECRUITMENT_PROFILES}?allData=true`)
        setJobOpenings(result?.data?.data || [])
      } catch (e) {
        console.error("Failed to fetch job openings:", e)
      }
    }

    fetchJobOpenings()
  }, [dispatch])

  const isAnyFileUploading = Object.values(uploadingFiles).some(Boolean)
  useEffect(() => {
    if (id) {
      const fetchReferralData = async () => {
        try {
          const result = await getApi(API_PATHS.REFERRAL_LIST, id)
          setReferralData(result?.data)
          const updatedRes = {
            referringForJob: result?.data.referringForJob?._id || "",
            name: result?.data.name || "",
            email: result?.data.email || "",
            phoneNumber: result?.data.phoneNumber || "",
            currentEmployer: result?.data.currentEmployer || "",
            relationWithEmployee: result?.data.relationWithEmployee || "",
            referralFeedback: result?.data.referralFeedback || "",
            resume: result?.data.resume || "",
          }
          setOriginalReferralData(updatedRes)
        } catch (e) {
          toast.error(constant.somethingWrong)
        }
      }

      fetchReferralData()
    }
  }, [id])
  const handleFileSelection = (setFieldValue, setFieldError, fieldName) => async (e) => {
    const inputElement = e.target;

    const file = e.target.files[0]

    const clearInput = () => {
      if (inputElement) {
        inputElement.value = '';
      }
    }

    if (!file) {
      clearInput();
      return;
    }

    // Clear removal flag when new file is selected
    setRemovedFiles(prev => ({
      ...prev,
      [fieldName]: false
    }))

    setShowSizeError((prev) => ({
      ...prev,
      [fieldName]: false,
    }))
    // File size validation
    if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
      setFieldError(fieldName, `File size exceeds ${MAX_ATTACHMENT_FILE_SIZE_MB}MB`)
      setFieldValue(fieldName, '')
      setShowSizeError((prev) => ({
        ...prev,
        [fieldName]: true,
      }))
      clearInput();
      setPendingUploads(prev => ({
        ...prev,
        [fieldName]: null
      }))
      return
    }

    // File validation
    const validation = validateS3File(file, {
      maxSizeInMB: MAX_ATTACHMENT_FILE_SIZE_MB,
      allowedExtensions: ['pdf', 'doc', 'docx', 'txt', 'png', 'jpg', 'jpeg']
    })

    if (!validation.valid) {
      setFieldError(fieldName, validation.error)
      setFieldValue(fieldName, '')
      clearInput();
      setPendingUploads(prev => ({
        ...prev,
        [fieldName]: null
      }))
      return
    }
    if (isEditMode && id && referralData?.[fieldName]) {
      try {
        let user = null;
        const userStr = localStorage.getItem('user');
        if (userStr) {
          user = JSON.parse(userStr);
        }

        const existingFileKey = referralData[fieldName];
        const filename = existingFileKey.split('/').pop();
        const filePath = existingFileKey.replace(filename, '');

        const deleteData = [{
          fileName: filename,
          method: "DELETE",
          filePath: filePath,
          userId: user?._id || 'anonymous'
        }];

        // Get DELETE presigned URL
        const deletePresignedData = await s3HelperInstance.getPresignedUrl(
          filename,
          '',
          filePath,
          deleteData
        );

        // Store for execution on form submit
        setPendingDeletions(prev => ({
          ...prev,
          [fieldName]: {
            key: existingFileKey,
            presignedUrl: deletePresignedData[0].url,
            filename: filename
          }
        }));

      } catch (error) {
        console.error(`Failed to prepare DELETE for old ${fieldName}:`, error);
      }
    }
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const extension = file.name.split('.').pop();
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const filename = `${timestamp}-${baseName}.${extension}`;
      const moduleName = 'referrals'
      // Generate S3 path
      const s3Path = generateS3Path(moduleName, id);
      let user = null;
      const userStr = localStorage.getItem('user');
      if (userStr) {
        user = JSON.parse(userStr);
      }
      const data = [
        {
          fileName: filename,
          method: "PUT", // Request PUT URL for uploading
          filePath: s3Path,
          userId: user?._id || 'anonymous'
        }
      ]
      // Get pre-signed URL but don't upload yet
      const presignedData = await s3HelperInstance.getPresignedUrl(filename, file.type, s3Path, data);

      // Store file and presigned URL for later upload
      setPendingUploads(prev => ({
        ...prev,
        [fieldName]: {
          file: file,
          presignedUrl: presignedData[0].url,
          contentType: presignedData[0].contentType || file.type,
          filename: filename,
          originalName: file.name,
          key: presignedData[0].key
        }
      }))

      setFieldValue(fieldName, filename)
      setFieldError(fieldName, "")
      setShowSizeError((prev) => ({
        ...prev,
        [fieldName]: false,
      }))
      // Clear input to allow same file selection again
      clearInput();

    } catch (error) {
      console.error(`=== FILE PREPARATION ERROR: ${fieldName} ===`, error)
      setFieldError(fieldName, "Failed to prepare file. Please try again.")
      setFieldValue(fieldName, '')
      clearInput();
      setPendingUploads(prev => ({
        ...prev,
        [fieldName]: null
      }))
      toast.error(`Failed to prepare ${fileFieldMapping[fieldName]}`)
    }
  }

  const handleRemoveFile = async (setFieldValue, fieldName, setFieldError, setFieldTouched) => {
    const isExistingFile = isEditMode && id && referralData?.[fieldName];
    const isPendingFile = pendingUploads[fieldName];
    setPendingUploads(prev => ({
      ...prev,
      [fieldName]: null
    }))
    setFieldValue(fieldName, '')

    setRemovedFiles(prev => ({
      ...prev,
      [fieldName]: true
    }))

    if (isExistingFile && !isPendingFile) {
      try {
        let user = null;
        const userStr = localStorage.getItem('user');
        if (userStr) {
          user = JSON.parse(userStr);
        }

        const existingFileKey = referralData[fieldName];
        const filename = existingFileKey.split('/').pop();
        const filePath = existingFileKey.replace(filename, '');

        const deleteData = [{
          fileName: filename,
          method: "DELETE",
          filePath: filePath,
          userId: user?._id || 'anonymous'
        }];

        // Get DELETE presigned URL
        const deletePresignedData = await s3HelperInstance.getPresignedUrl(
          filename,
          '',
          filePath,
          deleteData
        );

        // Store deletion info for later execution
        setPendingDeletions(prev => ({
          ...prev,
          [fieldName]: {
            key: existingFileKey,
            presignedUrl: deletePresignedData[0].url,
            filename: filename
          }
        }));

        console.log(`DELETE presigned URL prepared for ${fieldName}:`, deletePresignedData[0].url);
      } catch (error) {
        console.error(`Failed to prepare DELETE presigned URL for ${fieldName}:`, error);
        toast.error(`Failed to prepare file deletion for ${fieldName}`);
      }
    }
    if (id && referralData?.[fieldName]) {
      setReferralData(prev => ({
        ...prev,
        [fieldName]: null
      }))
    }

    const inputElement = document.getElementById(`${fieldName}-upload`);
    if (inputElement) {
      inputElement.value = '';
    }

    // Add error message for required field
    if (fieldName === 'resume') {
      setFieldValue(fieldName, '');
    }
    setTimeout(() => {
      if (fieldName === 'resume') {
        setFieldTouched(fieldName, true, false);
        const fieldDisplayName = fieldName === 'resume' ? 'Resume' : 'Cover Letter';
        setFieldError(fieldName, `${fieldDisplayName} is required`);
      }
    }, 0);
  }
  const executeAllPendingDeletions = async () => {
    const deletionPromises = [];

    Object.entries(pendingDeletions).forEach(([fieldName, deletionData]) => {
      if (deletionData) {
        const deletionPromise = async () => {
          try {
            console.log(`Executing deletion for ${fieldName}:`, deletionData.key);

            // Execute DELETE using presigned URL
            await axios.delete(deletionData.presignedUrl);

            console.log(`Successfully deleted ${fieldName} from S3`);
            // toast.success(`${fieldName} removed successfully`);
          } catch (error) {
            console.error(`Failed to delete ${fieldName}:`, error);
            // Don't throw - log but continue with other operations
            console.warn(`Deletion failed for ${fieldName}, but continuing with update`);
          }
        };
        deletionPromises.push(deletionPromise());
      }
    });

    if (deletionPromises.length > 0) {
      await Promise.allSettled(deletionPromises); // Use allSettled to not fail entire operation
    }
  };
  const uploadAllPendingFiles = async () => {
    const uploadResults = {}
    const uploadPromises = []

    Object.entries(pendingUploads).forEach(([fieldName, uploadData]) => {
      if (uploadData) {
        const uploadPromise = async () => {
          try {
            setUploadingFiles(prev => ({ ...prev, [fieldName]: true }))

            const uploadResult = await s3HelperInstance.updatedUploadToS3(
              uploadData.file,
              uploadData.presignedUrl,
              (percent) => console.log(`Upload progress - ${fieldName}: ${percent}%`),
              uploadData.contentType
            )

            uploadResults[fieldName] = uploadResult.url
          } catch (error) {
            console.error(`Failed to upload ${fieldName}:`, error)
            throw new Error(`Failed to upload ${fieldName}: ${error.message}`)
          } finally {
            setUploadingFiles(prev => ({ ...prev, [fieldName]: false }))
          }
        }
        uploadPromises.push(uploadPromise())
      }
    })

    if (uploadPromises.length > 0) {
      await Promise.all(uploadPromises)
    }
    return uploadResults
  }
  const EditData = async (values) => {
    try {
      setEditLoading(true)

      const result = await dispatch(
        updateReferral({
          id,
          values,
          resetForm: null,
          navigate,
        }),
      )
    } catch (e) {
      toast.error(constant.somethingWrong)
    } finally {
      setEditLoading(false)
    }
  }

  // const handleDownloadAttachment = async (attachmentUrl, filename) => {
  //   try {
  //     await dispatch(downloadAttachment({ attachmentUrl, filename }))
  //   } catch (error) {
  //     console.error("Download attachment error:", error)
  //   }
  // }

  const handleDownloadWithS3Helper = async (fileUrl, fileName) => {
    try {
      let user = null;
      const userStr = localStorage.getItem('user');
      if (userStr) {
        user = JSON.parse(userStr);
      }
      let downloadUrl = fileUrl;
      const data = [
        {
          fileName: fileName,
          method: "GET",
          filePath: fileUrl.replace(fileName, ''),
          userId: user?._id || 'anonymous'
        }
      ]

      if (!fileUrl.startsWith('http')) {
        const urlData = await s3HelperInstance.getDownloadUrl(data);
        downloadUrl = urlData.url;
      }

      const response = await axios.get(downloadUrl, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = removeTimestamp(fileName) || 'download';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      toast.success('File downloaded successfully')
    } catch (error) {
      console.error('=== DOWNLOAD ERROR ===', error)
      toast.error('Failed to download file')
    }
  }

  const handleDownloadAttachment = async (attachmentUrl, filename) => {
    try {
      await handleDownloadWithS3Helper(attachmentUrl, filename);
    } catch (error) {
      console.error("Download attachment error:", error);
      try {
        await dispatch(downloadAttachment({ attachmentUrl, filename }));
      } catch (fallbackError) {
        console.error("Fallback download also failed:", fallbackError);
      }
    }
  };

  const handleFormSubmit = async (values, { resetForm, isValid, errors }) => {
    try {
      if (isEditMode && id) {
        await executeAllPendingDeletions();
      }

      // First upload all pending files
      const uploadedUrls = await uploadAllPendingFiles()

      const processedValues = { ...values }

      // Use uploaded URLs for new files
      Object.entries(uploadedUrls).forEach(([fieldName, url]) => {
        processedValues[fieldName] = extractS3KeyFlexible(url)
      })

      // For existing files that weren't replaced or removed, keep the existing values
      if (id) {
        if (referralData?.resume && !uploadedUrls.resume && !removedFiles.resume) {
          processedValues.resume = referralData.resume;
        }
      }
      Object.entries(pendingDeletions).forEach(([fieldName, deletionData]) => {
        if (deletionData && !uploadedUrls[fieldName]) {
          processedValues[fieldName] = null;
        }
      });
      // Apply capitalization to the name field before submission
      const cleanedValues = {
        ...processedValues,
        name: capitalizeWords(processedValues.name),
      }

      const attachmentFields = ["resume"]
      const attachmentValues = {}

      attachmentFields.forEach((field) => {
        attachmentValues[field] = cleanedValues[field]
      })

      const generalCleanedValues = cleanFormDataCandidate(cleanedValues)

      attachmentFields.forEach((field) => {
        generalCleanedValues[field] = attachmentValues[field]
      })


      if (id) {
        await EditData(generalCleanedValues)
      } else {
        await dispatch(
          addReferral({
            values: generalCleanedValues,
            resetForm,
            navigate,
          }),
        )
      }

      // Clear states after successful submission
      setPendingUploads({ resume: null })
      setRemovedFiles({ resume: false })
      setPendingDeletions({ resume: null }); // FIXED

    } catch (error) {
      console.error("Form submission error:", error)
      toast.error("Failed to save referral")
    }
  }
  return (
    <>
      <div >
        <ActionNavbar isCreatePage={true} />
        <Stack>
          <Box className="createJobOpening-container">
            <Box className="createJobOpening-mainContainer">
              <Box className="createJobOpening-breadCrumb">
                <ArrowBackIcon onClick={() => navigate("/recruitment/referrals")} fontSize="20px" cursor={"pointer"} />
                <Text className="createJobOpening-pageTitle">
                  {isOverviewMode ? "Overview" : id ? recruitConstant.editReferral : recruitConstant.createReferrals}
                </Text>
              </Box>
            </Box>

            <Formik
              enableReinitialize
              initialValues={
                referralData
                  ? {
                    referringForJob: referralData.referringForJob?._id || "",
                    name: referralData.name || "",
                    email: referralData.email || "",
                    phoneNumber: referralData.phoneNumber || "",
                    currentEmployer: referralData.currentEmployer || "",
                    experience: referralData.experience || "",
                    relationWithEmployee: referralData.relationWithEmployee || "",
                    referralFeedback: referralData.referralFeedback || "",
                    resume: referralData.resume || "",
                  }
                  : {
                    ...REFERRAL_INITIAL_VALUES,
                  }
              }
              validationSchema={referralValidationSchema}
              onSubmit={handleFormSubmit}
            >
              {({ values, setFieldValue, handleSubmit, errors, dirty, isValid, setErrors, setTouched }) => {
                // Calculate tab states for current form state
                const tabStates = getTabStates(values, dirty, isValid, activeTabIndex)
                const isDirty = isFormDirty(originalReferralData || REFERRAL_INITIAL_VALUES, values);

                return (
                  <Form onSubmit={handleSubmit}>
                    <Tabs
                      index={activeTabIndex}
                      onChange={(index) => handleTabChange(index, values, dirty, isValid)}
                      className="createJobOpening-tabsContainer"
                      variant="unstyled"
                      orientation="vertical"
                    >
                      {/* tab list  */}
                      <TabList w={"40%"} className="createJobOpening-tabListContainer">
                        {referralTabs.map(({ label, Icon }, index) => {
                          const tabState = tabStates[index]
                          const isCurrentTab = index === activeTabIndex

                          return (
                            <Tab
                              className="createJobOpening-tabs"
                              key={index}
                              _selected={{
                                color: colors.cta["100"],
                                borderLeft: "2px solid",
                                borderColor: colors.cta["100"],
                              }}
                              _hover={{
                                color: tabState.isAccessible ? colors.cta["100"] : "inherit",
                                cursor: tabState.isAccessible ? "pointer" : "not-allowed",
                              }}
                              _focus={{ boxShadow: "none" }}
                              display="flex"
                              alignItems="center"
                              gap="8px"
                              opacity={tabState.isAccessible ? 1 : 0.6}
                              cursor={tabState.isAccessible ? "pointer" : "not-allowed"}
                              isDisabled={!tabState.isAccessible}
                            >
                              <Icon size={20} />
                              {label}
                              {tabState.isValid && (
                                <Text fontSize="xs" color="green.500">
                                  ✓
                                </Text>
                              )}
                            </Tab>
                          )
                        })}
                      </TabList>

                      {/* tab panels */}
                      <TabPanels w={"60%"} className="createJobOpening-tabsPanelContainer">
                        {/* Personal Information tab */}
                        <TabPanel className="createJobOpening-tabsPanel">
                          <Box className="createJobOpening-tabsPanelBody">
                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="referringForJob">
                                  {recruitConstant.jobOpening}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="referringForJob">
                                  {({ field, meta, form }) => {
                                    // Transform jobOpenings data for ReactSelect
                                    const jobOptions = jobOpenings.map((job) => ({
                                      value: job._id,
                                      label: `${formatJobTitle(job.postingTitle)} - ${job.customId}`,
                                    }))

                                    const selectedOption =
                                      jobOptions.find((option) => option.value === field.value) || null

                                    return (
                                      <>
                                        <ReactSelect
                                          isDisabled={!canModify}
                                          name={field.name}
                                          value={selectedOption}
                                          onBlur={() => form.setFieldTouched(field.name, true)}
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          options={jobOptions}
                                          className="basic-single-select reactSelect"
                                          classNamePrefix="select"
                                          onChange={(selectedOption) => {
                                            const selectedValue = selectedOption ? selectedOption.value : ""
                                            form.setFieldValue(field.name, selectedValue)
                                          }}
                                          placeholder="Select Job Opening"
                                          isClearable
                                          isSearchable
                                          styles={{
                                            control: (provided) => ({
                                              ...provided,
                                              borderColor:
                                                meta.touched && meta.error ? colors.errorText : colors.borderColor,
                                            }),
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>{meta.error}</Text>
                                        )}
                                      </>
                                    )
                                  }}
                                </Field>
                              </Box>

                              <Box w={"50%"}>
                                <FormLabel htmlFor="name">
                                  {recruitConstant.name}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="name">
                                  {({ field, meta, form }) => (
                                    <>
                                      <Input
                                        isDisabled={!canModify}
                                        {...field}
                                        placeholder="Enter name"
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        onChange={(e) => {
                                          let value = e.target.value;
                                          // Prevent leading spaces
                                          if (value.startsWith(' ')) return;
                                          // Allow only letters and spaces
                                          if (/^[a-zA-Z\s]*$/.test(value)) {
                                            // Remove multiple consecutive spaces
                                            value = value.replace(/\s{2,}/g, ' ');
                                            form.setFieldValue(field.name, value);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          form.setFieldTouched(field.name, true);
                                          // Trim on blur
                                          if (e.target.value) {
                                            form.setFieldValue(field.name, e.target.value.trim());
                                          }
                                        }}
                                        maxLength={255}
                                      />
                                      {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>

                            <Flex gap={"20px"}>

                              <Box w={"50%"}>
                                <FormLabel htmlFor="email">
                                  {recruitConstant.email}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="email">
                                  {({ field, meta, form }) => (
                                    <>
                                      <Input
                                        isDisabled={!canModify}
                                        {...field}
                                        placeholder={recruitConstant.emailPlaceHolderText}
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        type="email"
                                        onChange={(e) => {
                                          let value = e.target.value;
                                          // Prevent leading spaces
                                          if (value.startsWith(' ')) return;
                                          form.setFieldValue(field.name, value);
                                        }}
                                        onBlur={(e) => {
                                          form.setFieldTouched(field.name, true);
                                          // Trim on blur
                                          if (e.target.value) {
                                            form.setFieldValue(field.name, e.target.value.trim());
                                          }
                                        }}
                                      />
                                      {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                    </>
                                  )}
                                </Field>
                              </Box>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="phoneNumber">
                                  {recruitConstant.phone}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="phoneNumber">
                                  {({ field, meta }) => (
                                    <>
                                      <Input
                                        isDisabled={!canModify}
                                        {...field}
                                        placeholder={recruitConstant.phonePlaceHolderText}
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        type="tel"
                                        maxLength={10}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '');
                                          if (value.length <= 10) {
                                            field.onChange(e);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          const allowedKeys = [
                                            "Backspace",
                                            "ArrowLeft",
                                            "ArrowRight",
                                            "Tab",
                                            "Delete",
                                            "+",
                                            " ", // plus and space
                                          ]
                                          const isCopyPaste =
                                            (e.ctrlKey || e.metaKey) &&
                                            ["c", "v", "x", "a"].includes(e.key.toLowerCase())
                                          const isNumber = /^[0-9]$/.test(e.key)
                                          if (isCopyPaste) return
                                          // Get current value and check length
                                          const currentValue = e.currentTarget.value
                                          const maxLength = 13 // Maximum length including the "+" if present

                                          // Prevent input if max length reached and not a control key
                                          if (currentValue.length >= maxLength && !allowedKeys.includes(e.key)) {
                                            e.preventDefault()
                                            return
                                          }
                                          if (!isNumber && !allowedKeys.includes(e.key)) {
                                            e.preventDefault()
                                          }
                                        }}
                                      />
                                      {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>

                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="currentEmployer">
                                  {recruitConstant.currentEmployer}
                                </FormLabel>
                                <Field name="currentEmployer">
                                  {({ field, meta, form }) => (
                                    <>
                                      <Input
                                        isDisabled={!canModify}
                                        {...field}
                                        placeholder={recruitConstant.enterCurrentEmployer}
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        onChange={(e) => {
                                          let value = e.target.value;
                                          // Prevent leading spaces
                                          if (value.startsWith(' ')) return;
                                          // Remove multiple consecutive spaces
                                          value = value.replace(/\s{2,}/g, ' ');
                                          form.setFieldValue(field.name, value);
                                        }}
                                        onBlur={(e) => {
                                          form.setFieldTouched(field.name, true);
                                          // Trim on blur
                                          if (e.target.value) {
                                            form.setFieldValue(field.name, e.target.value.trim());
                                          }
                                        }}
                                        maxLength={100}
                                      />
                                      {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                    </>
                                  )}
                                </Field>
                              </Box>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="experience">
                                  Experience
                                </FormLabel>
                                <Field name="experience">
                                  {({ field, meta, form }) => (
                                    <>
                                      <Input
                                        isDisabled={!canModify}
                                        {...field}
                                        type="number"
                                        placeholder="Enter experience in years"
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                        min={0}
                                        step={0.1}
                                        max={45}
                                      />
                                      {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>

                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="relationWithEmployee">
                                  Relation With Employee
                                  <Text as="span" color={colors.errorText} ml={1}>*</Text>
                                </FormLabel>
                                <Field name="relationWithEmployee">
                                  {({ field, meta, form }) => {
                                    const relationOptions = [
                                      { value: "Friend", label: "Friend" },
                                      { value: "Colleague", label: "Colleague" },
                                      { value: "Relative", label: "Relative" },
                                      { value: "Ex-Colleague", label: "Ex-Colleague" },
                                      { value: "Ex-Employee", label: "Ex-Employee" },
                                      { value: "Acquaintance", label: "Acquaintance" },
                                      { value: "Other", label: "Other" },
                                    ]

                                    const selectedOption =
                                      relationOptions.find((option) => option.value === field.value) || null

                                    return (
                                      <>
                                        <ReactSelect
                                          isDisabled={!canModify}
                                          isRequired={true}
                                          name={field.name}
                                          value={selectedOption}
                                          onBlur={() => form.setFieldTouched(field.name, true)}
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          options={relationOptions}
                                          className="basic-single-select reactSelect"
                                          classNamePrefix="select"
                                          onChange={(selectedOption) => {
                                            const selectedValue = selectedOption ? selectedOption.value : ""
                                            form.setFieldValue(field.name, selectedValue)
                                          }}
                                          placeholder="Select Relation"
                                          isClearable
                                          isSearchable
                                          styles={{
                                            control: (provided) => ({
                                              ...provided,
                                              borderColor:
                                                meta.touched && meta.error ? colors.errorText : colors.borderColor,
                                            }),
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>{meta.error}</Text>
                                        )}
                                      </>
                                    )
                                  }}
                                </Field>
                              </Box>
                              <Box w={"50%"}>
                                <Field name="resume">
                                  {({ form, meta }) => {
                                    const hasPendingFile = pendingUploads.resume
                                    const hasValidationError = meta.touched && (meta.error || showSizeError.resume)
                                    const wasFileRemoved = removedFiles.resume
                                    const hasExistingFile = id && referralData?.resume && !hasPendingFile && !wasFileRemoved && !hasValidationError
                                    const filename = hasExistingFile
                                      ? referralData?.resume?.split("/")?.pop()
                                      : (hasPendingFile && !hasValidationError)
                                        ? pendingUploads.resume.originalName
                                        : null

                                    return (
                                      <>
                                        <FileUpload
                                          isDisabled={!canModify}
                                          label={constant.resume}
                                          icon={AttachmentIcon}
                                          isRequired={true}
                                          selectedFile={
                                            hasPendingFile && !hasValidationError
                                              ? `Selected: ${pendingUploads.resume.originalName}`
                                              : hasExistingFile
                                                ? `Current: ${removeTimestamp(filename)}`
                                                : null
                                          }
                                          title={constant.attachment}
                                          inputId="resume-upload"
                                          handleFileChange={(e) => {
                                            form.setFieldTouched("resume", true);
                                            handleFileSelection(
                                              form.setFieldValue,
                                              form.setFieldError,
                                              "resume"
                                            )(e);
                                          }}
                                          errorText={
                                            meta.touched && showSizeError?.resume
                                              ? recruitConstant.fileSizeExceed2MB
                                              : meta.touched && meta.error
                                                ? meta.error
                                                : ""
                                          }
                                          type="file"
                                          borderColor={
                                            meta.touched && meta.error ? colors.errorText : colors.borderColor
                                          }
                                          accept={ACCEPTED_DOCUMENT_TYPES}
                                        />

                                        {(hasExistingFile || (hasPendingFile && !hasValidationError)) && (
                                          <Flex gap={2} mt={3} align="center" flexWrap="wrap">
                                            {hasExistingFile && (
                                              <Button
                                                size="sm"
                                                leftIcon={<DownloadIcon />}
                                                onClick={() => handleDownloadAttachment(referralData.resume, filename)}
                                                variant="outline"
                                              >
                                                Download
                                              </Button>
                                            )}
                                            <IconButton
                                              isDisabled={!canModify}
                                              size="sm"
                                              icon={<CloseIcon />}
                                              onClick={() => handleRemoveFile(form.setFieldValue, "resume", form.setFieldError, form.setFieldTouched)}
                                              variant="outline"
                                              colorScheme="red"
                                              aria-label="Remove file"
                                              title="Remove file"
                                            />
                                          </Flex>
                                        )}
                                      </>
                                    )
                                  }}
                                </Field>
                              </Box>
                            </Flex>

                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="referralFeedback">
                                  Feedback
                                </FormLabel>
                                <Field name="referralFeedback">
                                  {({ field, meta }) => (
                                    <>
                                      <Input
                                        isDisabled={!canModify}
                                        {...field}
                                        as="textarea"
                                        placeholder="Enter feedback (1-500 words)"
                                        rows={4}
                                        borderColor={meta.touched && meta.error ? colors.errorText : colors.borderColor}
                                      />
                                      {meta.touched && meta.error && <Text color={colors.errorText}>{meta.error}</Text>}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>

                          </Box>

                          <Box className="createJobOpening-tabsPanelFooter">
                            <Button
                              className="theme-btn2 cancelBtn"
                              sx={{
                                textTransform: "capitalize",
                              }}
                              variant="outline"
                              colorScheme={colors.error["100"]}
                              size="sm"
                              onClick={() => navigate("/recruitment/referrals")}
                            >
                              {recruitConstant.cancel}
                            </Button>
                            {!isOverviewMode && (
                              <Button
                                disabled={
                                  addLoading ||
                                  editLoading ||
                                  (!id && !dirty) ||
                                  !isValid ||
                                  !canModify ||
                                  isAnyFileUploading ||
                                  !isTabValid(values, 0) ||
                                  (isEditMode && !isDirty)
                                }
                                className="nextBtn"
                                variant="primary"
                                type="submit"
                              >
                                {addLoading || editLoading || isAnyFileUploading ? (
                                  <Spinner size="sm" />
                                ) : id ? (
                                  constant.update
                                ) : (
                                  constant.save
                                )}
                              </Button>
                            )}
                            {isOverviewMode && (
                              <Button
                                onClick={() => navigate("/recruitment/referrals")}
                                className="nextBtn"
                                variant="primary"
                                type="button"
                              >
                                Return to Referrals
                              </Button>
                            )}
                          </Box>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </Form>
                )
              }}
            </Formik>
          </Box>
        </Stack>
      </div>
    </>
  )
}

export default ReferralsCreate
